import { Text } from "components/text";
import { KeyValueRow } from "./KeyValueRow";

export function humanizeKey(key: string): string {
  return key
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === "object" &&
    !(value instanceof Date) &&
    Object.keys(value).length === 0
  )
    return true;
  return false;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function formatValue(value: unknown): string {
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function isValueContainer(
  v: unknown,
): v is Record<string, unknown> & { value: string | number | boolean } {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  return (
    keys.length === 1 &&
    keys[0] === "value" &&
    isPrimitive((v as Record<string, unknown>).value)
  );
}

export function renderFields(
  obj: Record<string, unknown>,
  keyPrefix = "",
): React.ReactElement[] {
  return Object.entries(obj)
    .filter(([, value]) => !isEmpty(value))
    .flatMap<React.ReactElement>(([key, value]) => {
      const reactKey = keyPrefix ? `${keyPrefix}.${key}` : key;
      const label = humanizeKey(key);

      if (isValueContainer(value)) {
        return [
          <KeyValueRow
            key={reactKey}
            label={label}
            value={formatValue(value.value)}
          />,
        ];
      }

      if (isPrimitive(value) || value instanceof Date) {
        return [
          <KeyValueRow
            key={reactKey}
            label={label}
            value={formatValue(value)}
          />,
        ];
      }

      if (Array.isArray(value)) {
        if (value.every(isPrimitive)) {
          return [
            <KeyValueRow
              key={reactKey}
              label={label}
              value={value.join(", ")}
            />,
          ];
        }

        if (value.every(isValueContainer)) {
          return [
            <KeyValueRow
              key={reactKey}
              label={label}
              value={value.map((item) => formatValue(item.value)).join(", ")}
            />,
          ];
        }

        return value.map((item, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: values have no stable id and are not reordered
            key={`${reactKey}-${index}`}
            className="border-b border-secondary-100 last:border-0 py-1"
          >
            {typeof item === "object" && item !== null
              ? renderFields(
                  item as Record<string, unknown>,
                  `${reactKey}-${index}`,
                )
              : null}
          </div>
        ));
      }

      if (typeof value === "object" && value !== null) {
        const entries = Object.entries(value as Record<string, unknown>);

        if (entries.length > 0 && entries.every(([, v]) => Array.isArray(v))) {
          return entries.map(([groupKey, items]) => (
            <div
              key={`${reactKey}.${groupKey}`}
              className="border-b border-secondary-100 last:border-0 py-1"
            >
              <Text variant="captionStrong" className="mb-1">
                {groupKey}
              </Text>
              {(items as unknown[]).map((item, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: values have no stable id and are not reordered
                  key={`${reactKey}.${groupKey}-${index}`}
                  className="border-b border-secondary-100 last:border-0 py-1"
                >
                  {typeof item === "object" && item !== null
                    ? renderFields(
                        item as Record<string, unknown>,
                        `${reactKey}.${groupKey}-${index}`,
                      )
                    : null}
                </div>
              ))}
            </div>
          ));
        }

        return renderFields(value as Record<string, unknown>, reactKey);
      }

      return [];
    });
}
